import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Typography,
} from "@suraasa/placebo-ui"
import { Accordion } from "@suraasa/placebo-ui"
import {
  GradeBookScore,
  ScoreItemType,
} from "api/resources/learningItems/types"
import clsx from "clsx"
import { Lock, NavArrowDown } from "iconoir-react"
import { useState } from "react"
import { learningItemSlugs } from "utils/constants"
import { generateId, useScrollToField } from "utils/hooks/useScrollToField"

import { getAdjustedScore, truncateScore } from "../scoreHelpers"

const getTitle = (item: ScoreItemType) => {
  switch (item) {
    case "attendance":
      return "Attendance"
    case "assignment":
      return "Assignment Score"
    case "live_assessment":
      return "Assessment Score"
    case "self_paced":
      return "Self paced Score"

    default:
      return ""
  }
}
const Item = ({
  title,
  value,
  highlight,
}: {
  title: string
  value: string
  highlight?: boolean
}) => {
  return (
    <div className="flex grow flex-col gap-0.25">
      <span
        className={clsx("text-base font-bold leading-[20.8px]", {
          "text-primary-500": highlight,
          "text-onSurface-900": !highlight,
        })}
      >
        {value}
      </span>
      <span className="text-xs font-medium leading-[15.6px] text-onSurface-500">
        {title}
      </span>
    </div>
  )
}

const CourseItem = ({
  data,
  slug,
  isNewDeliveryMode,
}: {
  slug: string
  data: GradeBookScore["items"][0]
  isNewDeliveryMode: boolean
}) => {
  const isDisabled = data.score === null

  const { elementId: id } = useScrollToField(data.slug)
  const hasScrollToField = generateId(data.slug) === id

  const gradePoints = (() => {
    if (data.score === null) return null
    if (slug === learningItemSlugs.med) {
      return getAdjustedScore(data.score.gpa, slug)
    }
    return data.score.gpa
  })()

  const totalPoints =
    data.score?.pointsEarned != null
      ? getAdjustedScore(data.score.pointsEarned, slug)
      : null

  const [singleExpand, setSingleExpanded] = useState<string | undefined>(
    hasScrollToField ? generateId(data.slug) : undefined
  )

  const inProgress = data.score && data.score.creditsEarned === null

  return (
    <Accordion
      type="single"
      className="overflow-hidden rounded-xl border border-solid border-surface-200"
      defaultValue={singleExpand}
      onValueChange={setSingleExpanded}
      disabled={isDisabled}
    >
      <AccordionItem value={generateId(data.slug)}>
        <AccordionTrigger
          className={clsx(
            "!p-2 text-start text-white [&>.AccordionHeader-content]:w-full",
            {
              "bg-onSurface-800 ": !isDisabled,
              "bg-onSurface-400 !cursor-default": isDisabled,
            }
          )}
          icon={
            <div className="flex size-3 items-center justify-center rounded-full bg-white">
              {isDisabled ? (
                <Lock className="size-2 text-onSurface-400" />
              ) : (
                <NavArrowDown className="size-2 text-onSurface-900" />
              )}
            </div>
          }
        >
          <div className="inline-grid w-full flex-col items-start justify-between gap-1 md:flex md:flex-row md:items-center">
            <Typography className="max-w-[75%]" variant="title4">
              {data.name}
            </Typography>
            {!isDisabled && (
              <div className="mr-0 flex flex-wrap items-center gap-2 sm:mr-1">
                <span className="block text-xs font-medium leading-[15.6px]">
                  <span className="pr-0.5 text-base font-bold leading-[20.8px]">
                    {gradePoints != null ? truncateScore(gradePoints) : "-"}
                  </span>
                  Grade Points
                </span>
                <span className="block  text-xs font-medium leading-[15.6px]">
                  <span className="pr-0.5 text-base font-bold leading-[20.8px]">
                    {data.score?.grade ?? "-"}
                  </span>
                  Grade
                </span>
                <span className="block  text-xs font-medium leading-[15.6px]">
                  <span className="pr-0.5 text-base font-bold leading-[20.8px]">
                    {data.score?.creditsEarned ?? "-"}
                  </span>
                  Credits Earned
                </span>
              </div>
            )}
          </div>
        </AccordionTrigger>

        <AccordionContent
          className="!p-0"
          // className={clsx({
          //   "!px-2 !py-1.5":
          //     data.score === null || data.score.userItemScores.length === 0,
          //   "!px-3 !py-2.5": data.score !== null,
          // })}
        >
          {inProgress && (
            <div className="px-2 py-1.5">
              <span className="text-xs font-normal leading-[15.6px] text-onSurface-500">
                This course is in progress. You will be able to see your score,
                grade, and credits after you complete it.
              </span>
            </div>
          )}

          {!inProgress && (
            <div className="flex h-full min-h-5 flex-col gap-3 px-3 py-2.5 sm:flex-row sm:items-center">
              {totalPoints !== null && (
                <div className="flex flex-col gap-3 sm:flex-row">
                  <Item
                    value={Math.round(totalPoints).toString()}
                    highlight
                    title="Total Points"
                  />
                  <div className="h-px w-full rounded-full bg-onSurface-200  sm:h-full sm:min-h-5 sm:w-px " />
                </div>
              )}

              <div className="grid grid-cols-2 grid-rows-2 gap-3 sm:grid-cols-4 sm:grid-rows-1">
                {data.score?.userItemScores
                  .sort((a, b) => a.scoreType.localeCompare(b.scoreType))
                  .map((item, index) => {
                    const score =
                      item.score < 0 ? null : `${Math.round(item.score * 100)}%`

                    {
                      if (item.scoreType === "attendance" && isNewDeliveryMode)
                        return
                    }

                    return (
                      <Item
                        value={score || "-"}
                        title={getTitle(item.scoreType)}
                        key={index}
                      />
                    )
                  })}
              </div>
            </div>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}

export default CourseItem
